import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Image as ImageIcon, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DeliveryTypeEnum } from "@/types/DeliveryType";
import { ProductTypeEnum } from "@/types/ProductType";
import { productTypeSelectOptions } from "@/lib/productTypeDisplay";
import { ImageSelectDialog } from "@/components/ImageSelectDialog";
import type { ProductEditAudioFile, ProductEditImage, ProductEditLink, ProductEditPageProduct } from "@/routes/productEditTypes";
import { formatCentsAsUsd } from "@/lib/money";
import { ProductEditAccessTab } from "@/routes/ProductEditAccessTab";
import { ProductEditMeetingTab } from "@/routes/ProductEditMeetingTab";
import { ProductEditShopTab } from "@/routes/ProductEditShopTab";
import { ProductEditRecordingsTab } from "@/routes/ProductEditRecordingsTab";
import { ProductEditTrackTab } from "@/routes/ProductEditTrackTab";
import { ProductEditGatingTab } from "@/routes/ProductEditGatingTab";
import { ProductEditFlowTab } from "@/routes/ProductEditFlowTab/ProductEditFlowTab";
import {
  productEditTabPath,
  type ProductEditTab,
} from "@/routes/productEditTabs";

const PRODUCT_TYPE_NONE = "__none__";

export function ProductEditNoVariants({
  productId,
  product,
  activeTab,
  hasShopify,
  hasKunaki,
  onFlowChange,
}: {
  productId: string;
  product: ProductEditPageProduct;
  activeTab: ProductEditTab;
  hasShopify: boolean;
  hasKunaki: boolean;
  onFlowChange?: () => void;
}) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    price: "",
    payWhatYouCan: false,
    productType: PRODUCT_TYPE_NONE,
    deliveryType: DeliveryTypeEnum.PHYSICAL as DeliveryTypeEnum,
    shopifyProductId: "",
    kunakiProductId: "",
    images: [] as ProductEditImage[],
    coverArt: null as ProductEditImage | null,
    audioFiles: [] as ProductEditAudioFile[],
    links: [] as ProductEditLink[],
    isrc: "",
    buyDigitalEnabled: false,
    buyDigitalText: "",
    slug: "",
    inventoryQuantity: "",
    quantityMinimum: "",
    quantityMaximum: "",
  });
  const [isImageDialogOpen, setIsImageDialogOpen] = useState(false);

  useEffect(() => {
    setFormData({
      name: product.name || "",
      description: product.description || "",
      price:
        product.price !== undefined && product.price !== null
          ? formatCentsAsUsd(product.price)
          : "",
      payWhatYouCan: product.payWhatYouCan ?? false,
      productType:
        product.type &&
        (Object.values(ProductTypeEnum) as string[]).includes(product.type)
          ? product.type
          : PRODUCT_TYPE_NONE,
      deliveryType:
        product.deliveryType === DeliveryTypeEnum.DIGITAL ||
        product.deliveryType === DeliveryTypeEnum.PHYSICAL
          ? (product.deliveryType as DeliveryTypeEnum)
          : DeliveryTypeEnum.PHYSICAL,
      shopifyProductId: product.shopify?.productId || "",
      kunakiProductId: product.kunaki?.productId || "",
      images: product.images || [],
      coverArt: product.images?.[0] ?? null,
      audioFiles: product.audioFiles ?? [],
      links: product.links ?? [],
      isrc: product.track?.isrc ?? "",
      buyDigitalEnabled: product.track?.buyDigital?.enabled === true,
      buyDigitalText: product.track?.buyDigital?.text ?? "",
      slug: product.slug ?? "",
      inventoryQuantity:
        product.inventoryQuantity !== undefined && product.inventoryQuantity !== null
          ? String(product.inventoryQuantity)
          : "",
      quantityMinimum: product.quantityRules?.minimum?.toString() ?? "",
      quantityMaximum: product.quantityRules?.maximum?.toString() ?? "",
    });
  }, [product]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setIsSubmitting(true);
    try {
      const payload: Record<string, unknown> = {
        name: formData.name || undefined,
        description: formData.description.trim()
          ? formData.description
          : null,
        payWhatYouCan: formData.payWhatYouCan,
        deliveryType: formData.deliveryType || undefined,
        images:
          formData.productType === ProductTypeEnum.TRACK
            ? formData.coverArt
              ? [formData.coverArt]
              : []
            : formData.images,
        audioFiles:
          formData.productType === ProductTypeEnum.TRACK
            ? formData.audioFiles
            : undefined,
        links:
          formData.productType === ProductTypeEnum.TRACK
            ? formData.links.filter((link) => {
                const label = link.label.trim();
                const ctaText = link.ctaText?.trim() ?? "";
                return link.url.trim() && (label || ctaText);
              })
            : undefined,
        slug:
          formData.productType === ProductTypeEnum.TRACK
            ? formData.slug.trim() || null
            : undefined,
        track:
          formData.productType === ProductTypeEnum.TRACK
            ? {
                isrc: formData.isrc.trim() || null,
                buyDigital: {
                  enabled: formData.buyDigitalEnabled,
                  ...(formData.buyDigitalText.trim()
                    ? { text: formData.buyDigitalText.trim() }
                    : {}),
                },
              }
            : undefined,
      };

      if (formData.price.trim() !== "") {
        const priceValue = parseFloat(formData.price);
        if (!Number.isNaN(priceValue) && priceValue >= 0) {
          payload.price = Math.round(priceValue * 100);
        }
      }

      payload.shopify = formData.shopifyProductId.trim()
        ? { productId: formData.shopifyProductId.trim() }
        : null;

      payload.kunaki = formData.kunakiProductId.trim()
        ? { productId: formData.kunakiProductId.trim() }
        : null;

      const minNum = formData.quantityMinimum.trim()
        ? parseInt(formData.quantityMinimum, 10)
        : undefined;
      const maxNum = formData.quantityMaximum.trim()
        ? parseInt(formData.quantityMaximum, 10)
        : undefined;
      if (
        (minNum !== undefined && !isNaN(minNum)) ||
        (maxNum !== undefined && !isNaN(maxNum))
      ) {
        payload.quantityRules = {
          ...(minNum !== undefined && !isNaN(minNum) && { minimum: minNum }),
          ...(maxNum !== undefined && !isNaN(maxNum) && { maximum: maxNum }),
        };
      } else {
        payload.quantityRules = null;
      }

      if (formData.inventoryQuantity.trim() !== "") {
        const inv = parseInt(formData.inventoryQuantity, 10);
        if (!Number.isNaN(inv) && inv >= 0) {
          payload.inventoryQuantity = inv;
        }
      } else {
        payload.inventoryQuantity = null;
      }

      const response = await fetch(`/api/products/${productId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const data = await response.json();
        console.error("Failed to update product:", data.error);
        alert(data.error || "Failed to update product");
      }
    } catch (error) {
      console.error("Failed to update product:", error);
      alert("An error occurred while updating the product");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="p-4">
      <Card>
        <CardContent className="pt-6">
          <Tabs value={activeTab} className="w-full">
            <TabsList>
              <TabsTrigger asChild value="product">
                <Link to={productEditTabPath({ productId, tab: "product" })}>
                  Product
                </Link>
              </TabsTrigger>
              {formData.productType === ProductTypeEnum.MEETING && (
                <TabsTrigger asChild value="meeting">
                  <Link to={productEditTabPath({ productId, tab: "meeting" })}>
                    Meeting
                  </Link>
                </TabsTrigger>
              )}
              {formData.productType === ProductTypeEnum.MEETING && (
                <TabsTrigger asChild value="access">
                  <Link to={productEditTabPath({ productId, tab: "access" })}>
                    Access
                  </Link>
                </TabsTrigger>
              )}
              {formData.productType === ProductTypeEnum.MEETING && (
                <TabsTrigger asChild value="shop">
                  <Link to={productEditTabPath({ productId, tab: "shop" })}>
                    Shop
                  </Link>
                </TabsTrigger>
              )}
              {formData.productType === ProductTypeEnum.MEETING && (
                <TabsTrigger asChild value="recordings">
                  <Link
                    to={productEditTabPath({ productId, tab: "recordings" })}
                  >
                    Recordings
                  </Link>
                </TabsTrigger>
              )}
              {formData.productType === ProductTypeEnum.TRACK && (
                <TabsTrigger asChild value="track">
                  <Link to={productEditTabPath({ productId, tab: "track" })}>
                    Track
                  </Link>
                </TabsTrigger>
              )}
              {formData.productType === ProductTypeEnum.TRACK && (
                <TabsTrigger asChild value="gating">
                  <Link to={productEditTabPath({ productId, tab: "gating" })}>
                    Gating
                  </Link>
                </TabsTrigger>
              )}
              {(formData.productType === ProductTypeEnum.TRACK ||
                formData.productType === ProductTypeEnum.MEETING) && (
                <TabsTrigger asChild value="flow">
                  <Link to={productEditTabPath({ productId, tab: "flow" })}>
                    Flow
                  </Link>
                </TabsTrigger>
              )}
              {hasShopify && (
                <TabsTrigger asChild value="shopify">
                  <Link to={productEditTabPath({ productId, tab: "shopify" })}>
                    Shopify
                  </Link>
                </TabsTrigger>
              )}
              {hasKunaki && (
                <TabsTrigger asChild value="kunaki">
                  <Link to={productEditTabPath({ productId, tab: "kunaki" })}>
                    Kunaki
                  </Link>
                </TabsTrigger>
              )}
            </TabsList>

            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <TabsContent value="product" className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="productType">Product type</Label>
                  <Select value={formData.productType} disabled>
                    <SelectTrigger id="productType">
                      <SelectValue placeholder="Unset" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={PRODUCT_TYPE_NONE}>Unset</SelectItem>
                      {productTypeSelectOptions.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="deliveryType">Delivery Type</Label>
                  <Select
                    value={formData.deliveryType}
                    onValueChange={(value) =>
                      setFormData((prev) => ({
                        ...prev,
                        deliveryType: value as DeliveryTypeEnum,
                      }))
                    }
                  >
                    <SelectTrigger id="deliveryType">
                      <SelectValue placeholder="Select delivery type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={DeliveryTypeEnum.DIGITAL}>
                        Digital
                      </SelectItem>
                      <SelectItem value={DeliveryTypeEnum.PHYSICAL}>
                        Physical
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter product name"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows={5}
                    className="min-h-[100px] font-mono text-sm"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="price">Price</Label>
                  <Input
                    id="price"
                    name="price"
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={handleChange}
                    placeholder="Enter price"
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="payWhatYouCan"
                    checked={formData.payWhatYouCan}
                    onCheckedChange={(checked) =>
                      setFormData((prev) => ({
                        ...prev,
                        payWhatYouCan: checked === true,
                      }))
                    }
                  />
                  <Label htmlFor="payWhatYouCan" className="cursor-pointer">
                    Pay what you can
                  </Label>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="inventoryQuantity">Inventory quantity</Label>
                  <Input
                    id="inventoryQuantity"
                    name="inventoryQuantity"
                    type="number"
                    min={0}
                    value={formData.inventoryQuantity}
                    onChange={handleChange}
                    placeholder="Leave empty for unlimited"
                  />
                  <p className="text-xs text-muted-foreground">
                    When set, this count decreases when orders complete. Clear the
                    field to stop tracking inventory for this product.
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="quantityMinimum">Quantity minimum</Label>
                    <Input
                      id="quantityMinimum"
                      name="quantityMinimum"
                      type="number"
                      min={1}
                      value={formData.quantityMinimum}
                      onChange={handleChange}
                      placeholder="1"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="quantityMaximum">Quantity maximum</Label>
                    <Input
                      id="quantityMaximum"
                      name="quantityMaximum"
                      type="number"
                      min={1}
                      value={formData.quantityMaximum}
                      onChange={handleChange}
                      placeholder="10"
                    />
                  </div>
                </div>

                {formData.productType !== ProductTypeEnum.TRACK && (
                <div className="space-y-2">
                  <Label>Images</Label>
                  <div className="space-y-2">
                    {formData.images.length > 0 && (
                      <div className="grid grid-cols-3 gap-4">
                        {formData.images.map((img, index) => (
                          <div
                            key={`${img.fileId}-${index}`}
                            className="relative aspect-video bg-gray-100 rounded-md overflow-hidden border border-gray-200"
                          >
                            <img
                              src={img.url}
                              alt=""
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.display =
                                  "none";
                              }}
                            />
                            <Button
                              type="button"
                              variant="destructive"
                              size="icon"
                              className="absolute top-2 right-2 h-6 w-6"
                              onClick={() =>
                                setFormData((prev) => ({
                                  ...prev,
                                  images: prev.images.filter((_, i) => i !== index),
                                }))
                              }
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setIsImageDialogOpen(true)}
                      className="w-full"
                    >
                      <ImageIcon className="h-4 w-4 mr-2" />
                      {formData.images.length > 0 ? "Add Image" : "Select Image"}
                    </Button>
                  </div>
                </div>
                )}
              </TabsContent>

              {formData.productType === ProductTypeEnum.MEETING && (
                <TabsContent value="meeting">
                  <ProductEditMeetingTab productId={productId} product={product} />
                </TabsContent>
              )}

              {formData.productType === ProductTypeEnum.MEETING && (
                <TabsContent value="access">
                  <ProductEditAccessTab productId={productId} product={product} />
                </TabsContent>
              )}

              {formData.productType === ProductTypeEnum.MEETING && (
                <TabsContent value="shop">
                  <ProductEditShopTab productId={productId} product={product} />
                </TabsContent>
              )}

              {formData.productType === ProductTypeEnum.MEETING && (
                <TabsContent value="recordings">
                  <ProductEditRecordingsTab productId={productId} product={product} />
                </TabsContent>
              )}

              {formData.productType === ProductTypeEnum.TRACK && (
                <TabsContent value="track">
                  <ProductEditTrackTab
                    slug={formData.slug}
                    onSlugChange={(slug) =>
                      setFormData((prev) => ({ ...prev, slug }))
                    }
                    coverArt={formData.coverArt}
                    onCoverArtChange={(coverArt) =>
                      setFormData((prev) => ({ ...prev, coverArt }))
                    }
                    audioFiles={formData.audioFiles}
                    onAudioFilesChange={(audioFiles) =>
                      setFormData((prev) => ({ ...prev, audioFiles }))
                    }
                    isrc={formData.isrc}
                    onIsrcChange={(isrc) =>
                      setFormData((prev) => ({ ...prev, isrc }))
                    }
                    links={formData.links}
                    onLinksChange={(links) =>
                      setFormData((prev) => ({ ...prev, links }))
                    }
                    buyDigitalEnabled={formData.buyDigitalEnabled}
                    onBuyDigitalEnabledChange={(buyDigitalEnabled) =>
                      setFormData((prev) => ({ ...prev, buyDigitalEnabled }))
                    }
                    buyDigitalText={formData.buyDigitalText}
                    onBuyDigitalTextChange={(buyDigitalText) =>
                      setFormData((prev) => ({ ...prev, buyDigitalText }))
                    }
                  />
                </TabsContent>
              )}

              {formData.productType === ProductTypeEnum.TRACK && (
                <TabsContent value="gating">
                  <ProductEditGatingTab productId={productId} product={product} />
                </TabsContent>
              )}

              {(formData.productType === ProductTypeEnum.TRACK ||
                formData.productType === ProductTypeEnum.MEETING) && (
                <TabsContent value="flow">
                  <ProductEditFlowTab
                    productId={productId}
                    productType={formData.productType}
                    startPageId={product.flow?.startPageId}
                    accessOnSuccessActions={
                      product.meeting?.access?.checkout?.onSuccessActions
                    }
                    shopOnSuccessActions={
                      product.meeting?.shop?.checkout?.onSuccessActions
                    }
                    onFlowChange={onFlowChange}
                  />
                </TabsContent>
              )}

              {hasShopify && (
                <TabsContent value="shopify" className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="shopifyProductId">Shopify Product ID</Label>
                    <Input
                      id="shopifyProductId"
                      name="shopifyProductId"
                      value={formData.shopifyProductId}
                      onChange={handleChange}
                      placeholder="Enter Shopify product ID"
                    />
                  </div>
                </TabsContent>
              )}

              {hasKunaki && (
                <TabsContent value="kunaki" className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="kunakiProductId">Kunaki Product ID</Label>
                    <Input
                      id="kunakiProductId"
                      name="kunakiProductId"
                      value={formData.kunakiProductId}
                      onChange={handleChange}
                      placeholder="Enter Kunaki product ID"
                    />
                  </div>
                </TabsContent>
              )}

              <div className="flex gap-2 justify-end pt-4">
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Saving..." : "Save"}
                </Button>
              </div>
            </form>
          </Tabs>
        </CardContent>
      </Card>

      <ImageSelectDialog
        open={isImageDialogOpen}
        onOpenChange={setIsImageDialogOpen}
        onSelect={({ fileId, url }) =>
          setFormData((prev) => ({
            ...prev,
            images: [...prev.images, { fileId, url }],
          }))
        }
        title="Select Product Image"
        description="Choose an image from your uploaded files or upload a new one"
      />
    </div>
  );
}
